import React, { useState, useRef, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  countryOptions,
  searchCriteriaOptions,
  matchToOptions,
} from "../constants/data";

const CustomDropdown = ({ value, options, onChange, placeholder, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);
  const label = selected?.name || placeholder || "Select";
  const hasValue = value && value !== "";

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-xs font-semibold min-w-[150px] focus:outline-none transition-colors disabled:opacity-80 disabled:cursor-not-allowed ${
          hasValue
            ? "bg-primary text-white border-primary"
            : "bg-white text-muted border-primary"
        }`}
      >
        <span className="truncate">{label}</span>
        <KeyboardArrowDownIcon
          fontSize="small"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Popover */}
      {open && !disabled && (
        <div className="absolute z-50 mt-1 left-0 bg-white rounded-2xl shadow-xl border border-line min-w-[220px] p-3 overflow-hidden">
          {/* Header row */}
          <div className="flex items-center justify-between gap-2 px-4 py-2.5 mb-2 rounded-xl bg-primary text-white text-xs font-semibold">
            <span>{placeholder || "Select"}</span>
            <KeyboardArrowDownIcon fontSize="small" className="rotate-180" />
          </div>

          {/* Options */}
          {options
            .filter((optn) => optn.value !== "")
            .map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 rounded-xl text-xs transition-colors border mb-1.5 last:mb-0 ${
                  opt.value === value
                    ? "bg-primary text-white font-semibold border-primary"
                    : "bg-white text-primary border-line hover:bg-light"
                }`}
              >
                {opt.name}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

const BuyNumberSection = ({
  selectedCountry,
  setSelectedCountry,
  searchCriteria,
  setSearchCriteria,
  searchDigits,
  setSearchDigits,
  matchTo,
  setMatchTo,
  onSearch,
  onReset,
}) => {
  return (
    <div className="sm:mx-2 mb-6 border border-line rounded-xl bg-white shadow-sm p-5">
      <h2 className="text-base font-bold text-primary mb-4">Buy a Number</h2>

      {/* Country Selector */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-content mb-1">
          Country
        </label>
        <div className="relative">
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="w-full border border-content rounded-lg px-3 py-2.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white pr-10"
          >
            {countryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.name}
              </option>
            ))}
          </select>
          <SearchIcon
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            fontSize="small"
          />
        </div>
      </div>

      {/* Search Criteria & Match To Row */}
      <div className="flex flex-col sm:flex-row gap-4 mb-2">
        <div>
          <label className="block text-xs font-semibold text-content mb-1.5">
            Search Criteria
          </label>
          <div className="flex flex-row max-[500px]:flex-col sm:items-center gap-2">
            <CustomDropdown
              value={searchCriteria}
              options={searchCriteriaOptions}
              onChange={setSearchCriteria}
              placeholder="Select Criteria"
              disabled={searchCriteriaOptions.length <= 2}
            />
            <input
              type="text"
              value={searchDigits}
              onChange={(e) => setSearchDigits(e.target.value)}
              placeholder="Search by digits or phrases"
              className="border border-primary rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-[200px]"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-content mb-1.5">
            Match To
          </label>
          <CustomDropdown
            value={matchTo}
            options={matchToOptions}
            onChange={setMatchTo}
            placeholder="Select Part Of Number"
          />
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Search by area code, prefix, or characters you want in your phone
        number.
      </p>
      
      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          disabled={searchDigits.length === 0}
          onClick={onSearch}
          className="px-6 py-2 bg-primary text-white rounded-md text-xs font-bold tracking-wider hover:bg-primary/90 transition-colors disabled:cursor-not-allowed disabled:bg-primary/50"
        >
          SEARCH
        </button>
        <button
          onClick={onReset}
          className="px-6 py-2 border border-primary text-primary rounded-md text-xs font-bold tracking-wider hover:bg-light transition-colors"
        >
          RESET
        </button>
      </div>
    </div>
  );
};

export default BuyNumberSection;
